import { afterNextRender, AfterViewInit, computed, Directive, effect, ElementRef, inject, Injector, input, Renderer2, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs';
import { HisBaseGlobalsService } from '../../core/service/base/his-base-globals.service';
import { HisUiService } from '../../core/service/base/his-ui.service';
import { SvgService } from '../../core/service/svg/svg.service';
import { VoidCallable } from '../../core/utils';
import { HisColumnStateDirective, HisProjectedColumn } from './his-column-state.directive';
import { HisTableContextDirective } from './his-table-context.directive';

@Directive( {
    selector: 'th[hisReorderableColumn]',
    standalone: true
} )
export class HisReorderableColumnDirective implements AfterViewInit {
    private readonly columnState = inject( HisColumnStateDirective );
    private readonly tableContext = inject( HisTableContextDirective );

    private readonly host = inject<ElementRef<HTMLTableCellElement>>( ElementRef ); 
    private readonly renderer = inject( Renderer2 );
    private readonly injector = inject( Injector );

    private readonly hisBaseGlobalsService = inject( HisBaseGlobalsService );
    private readonly svgService = inject( SvgService );
    private readonly hisUiService = inject( HisUiService );

    readonly column = input.required<HisProjectedColumn>( { alias: 'hisReorderableColumn' } );
    private readonly columnLabel = computed( () => this.hisUiService.translate( this.column().label ) );

    private readonly arrowLeftSvg = this.getSvg( 'ARROW_LEFT_FAT' );
    private readonly arrowRightSvg = this.getSvg( 'ARROW_RIGHT_FAT' );

    private readonly moveLeftTitle = this.getTitle( 'common.table.moveColumn.left' );
    private readonly moveRightTitle = this.getTitle( 'common.table.moveColumn.right' );

    private readonly initialized = signal( false );
    private readonly updateEffect = effect( () => this.updateButtons() );

    private readonly buttons: {
        left?: HTMLButtonElement;
        right?: HTMLButtonElement;
    } = {};

    ngAfterViewInit() {
        this.initialized.set( true );
    }

    private getSvg( name: string ) {
        return toSignal( this.svgService.getSVG( '/HISinOne/images/' + this.hisBaseGlobalsService.getImage( name ) ).pipe( map( svg => {
            if (!svg) {
                return null;
            }
            const newSvg = svg.cloneNode( true ) as SVGElement;
            newSvg.classList.add( 'inlineicon' );
            return newSvg;
        } ) ) );
    }

    private getTitle( key: string ) {
        return computed( () => this.hisUiService.translate( key, this.columnLabel() ) );
    }

    private updateButtons() {
        if ( !this.initialized() || !this.arrowLeftSvg() || !this.arrowRightSvg() ) {
            return;
        }
        if ( !this.tableContext.tableConfig().columnsReorderable ) {
            this.removeButton( 'left' );
            this.removeButton( 'right' );
            return;
        }

        const header = this.host.nativeElement;
        if ( this.column().isFirst ) {
            this.removeButton( 'left' );
        } else if ( !this.buttons.left ) {
            const button = this.createButton( 'left', () => this.columnState.moveLeft( this.column() ) );
            this.renderer.insertBefore( header, button, header.firstChild );
        }

        if ( this.column().isLast ) {
            this.removeButton( 'right' );
        } else if ( !this.buttons.right ) {
            const button = this.createButton( 'right', () => this.columnState.moveRight( this.column() ) );
            this.renderer.appendChild( header, button );
        }
    }

    private createButton( buttonKey: keyof typeof this.buttons, eventHandler: VoidCallable ) {
        const button = this.renderer.createElement( 'button' ) as HTMLButtonElement;
        button.className = 'icon';
        button.title = buttonKey === 'left' ? this.moveLeftTitle() : this.moveRightTitle();
        button.ariaLabel = button.title;
        button.type = 'button';
        const completeHandler = ( event: Event ) => {
            event.stopImmediatePropagation();
            event.preventDefault();
            eventHandler();
            afterNextRender( () => {
                const target = this.buttons[buttonKey] ?? this.buttons[buttonKey === 'left' ? 'right' : 'left'];
                target?.focus();
            }, { injector: this.injector } );
        };
        button.addEventListener( 'click', completeHandler );
        button.addEventListener( 'keydown', event => {
            // prevent default action for Enter and Space keys (pSortableColumn)
            if ( event.key === 'Enter' || event.key === ' ' ) {
                completeHandler( event );
            }
        } );
        const svg = buttonKey === 'left' ? this.arrowLeftSvg() : this.arrowRightSvg();
        this.renderer.appendChild( button, svg );
        this.buttons[buttonKey] = button;
        return button;
    }

    private removeButton( button: keyof typeof this.buttons ) {
        if ( this.buttons[button] ) {
            this.renderer.removeChild( this.host.nativeElement, this.buttons[button] );
            this.buttons[button] = undefined;
        }
    }
}
